import { format } from 'date-fns';
import styled from 'styled-components';

import Button from 'src/app/components/Elements/Button';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { VerticalTracker } from 'src/app/components/Elements/Trackers/VerticalTracker';
import { Typography } from 'src/app/components/Elements/Typography';
import type { Dispatch } from 'src/app/state/store';
import { useDispatch } from 'src/app/state/store';
import type { PrescriptionLifecycle } from 'src/app/types';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import { ctaAction, CTATypesToTextMap } from '../utils';

const phaseIconToCapsuleIconMap = {
  active: <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.tracker_processing} />,
  needs_action: <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.tracker_needs_action} />,
  phone: undefined,
};

const LifecyclePhaseContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LifecycleStepContainer = styled.div`
  margin-bottom: ${props => props.theme.space.s2};
`;

const CTAButtonContainer = styled.div`
  margin-top: 1.6rem;
  & > button {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

export interface StatusSectionProps {
  prescriptionLifecycle: PrescriptionLifecycle;
}

const createTrackerSteps = (prescriptionLifecycle: PrescriptionLifecycle, dispatch: Dispatch) => {
  const { currentPhase } = prescriptionLifecycle;
  const trackerSteps = prescriptionLifecycle.phases.map((phase, phaseIdx) => {
    const isCurrentPhase = phaseIdx === currentPhase - 1;
    const steps = phase.steps.map((step, stepIdx) => {
      const formattedDate = format(new Date(step.dateCompleted), 'M/d/yy h:mma');
      const isCurrentStep = isCurrentPhase && stepIdx === phase.steps.length - 1;
      return (
        <LifecycleStepContainer key={stepIdx}>
          {step.dateCompleted && <Typography variant="overline">{formattedDate}</Typography>}
          <Typography variant={isCurrentStep ? 'h4' : 'body'}>{step.displayText}</Typography>
        </LifecycleStepContainer>
      );
    });
    const hasCTA = !!phase.cta;
    return (
      <LifecyclePhaseContainer>
        {steps}
        {hasCTA && (
          <CTAButtonContainer>
            <Button style="secondary" size="small" onClick={() => ctaAction(phase.cta, dispatch)}>
              {CTATypesToTextMap[phase.cta.action.type]}
            </Button>
          </CTAButtonContainer>
        )}
      </LifecyclePhaseContainer>
    );
  });
  return trackerSteps;
};

export const StatusSection = ({ prescriptionLifecycle }: StatusSectionProps) => {
  const dispatch = useDispatch();
  const { currentPhase, barState, currentPhaseIconState } = prescriptionLifecycle;

  const trackerVariant = barState === 'active' ? 'active' : 'inactive';

  const trackerSteps = createTrackerSteps(prescriptionLifecycle, dispatch);

  const iconOverride = currentPhaseIconState ? phaseIconToCapsuleIconMap[currentPhaseIconState] : undefined;

  return (
    <VerticalTracker
      variant={trackerVariant}
      steps={trackerSteps}
      currentStepIdx={currentPhase - 1}
      iconOverride={iconOverride}
    />
  );
};
